
import React from 'react';
import { PhoneCall } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const isMobile = useIsMobile();
  const phoneNumber = "+1 (860)-982-5635";
  const phoneNumberFormatted = phoneNumber.replace(/[^\d+]/g, "");
  const email = "Kristen.Tangarone@gmail.com";
  
  // Create a smooth scroll function similar to the one in Navbar
  const handleSmoothScroll = (e: React.MouseEvent<HTMLAnchorElement>, targetId: string) => {
    e.preventDefault();
    const targetElement = document.querySelector(targetId);
    
    if (targetElement) {
      // Smooth scroll to the target section with proper offset for fixed header
      const headerOffset = isMobile ? 90 : 120;
      const elementPosition = targetElement.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
      
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
      
      // Update URL without causing page jump
      window.history.pushState(null, '', targetId);
    }
  };
  
  return (
    <footer className="bg-charcoal text-white py-12">
      <div className="container">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          <div>
            <h3 className="font-heading font-semibold text-xl mb-4">KTJones Management Consulting</h3>
            <p className="text-gray-300 mb-4">
              Empowering businesses to scale confidently with strategic operational support and leadership development.
            </p>
            <div className="flex space-x-4">
              <a 
                href="https://www.linkedin.com/in/kristen-tangarone/" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-white hover:text-maroon transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"></path>
                  <rect x="2" y="9" width="4" height="12"></rect>
                  <circle cx="4" cy="4" r="2"></circle>
                </svg>
              </a>
            </div>
          </div>
          
          <div>
            <h3 className="font-heading font-semibold text-xl mb-4">Services</h3>
            <ul className="space-y-2">
              <li>
                <a 
                  href="#services" 
                  onClick={(e) => handleSmoothScroll(e, '#services')}
                  className="text-gray-300 hover:text-white transition-colors"
                >
                  Operational Efficiency
                </a>
              </li>
              <li>
                <a 
                  href="#services" 
                  onClick={(e) => handleSmoothScroll(e, '#services')}
                  className="text-gray-300 hover:text-white transition-colors"
                >
                  Leadership Development
                </a>
              </li>
              <li>
                <a 
                  href="#services" 
                  onClick={(e) => handleSmoothScroll(e, '#services')}
                  className="text-gray-300 hover:text-white transition-colors"
                >
                  Strategic Growth
                </a>
              </li>
              <li>
                <a 
                  href="#services" 
                  onClick={(e) => handleSmoothScroll(e, '#services')}
                  className="text-gray-300 hover:text-white transition-colors"
                >
                  Data-Driven Decision Making
                </a>
              </li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-heading font-semibold text-xl mb-4">Quick Links</h3>
            <ul className="space-y-2">
              <li>
                <a 
                  href="#about" 
                  onClick={(e) => handleSmoothScroll(e, '#about')}
                  className="text-gray-300 hover:text-white transition-colors"
                >
                  About
                </a>
              </li>
              <li>
                <a 
                  href="#services" 
                  onClick={(e) => handleSmoothScroll(e, '#services')}
                  className="text-gray-300 hover:text-white transition-colors"
                >
                  Services
                </a>
              </li>
              <li>
                <a 
                  href="#testimonials" 
                  onClick={(e) => handleSmoothScroll(e, '#testimonials')}
                  className="text-gray-300 hover:text-white transition-colors"
                >
                  Testimonials
                </a>
              </li>
              <li>
                <a 
                  href="#contact" 
                  onClick={(e) => handleSmoothScroll(e, '#contact')}
                  className="text-gray-300 hover:text-white transition-colors"
                >
                  Contact
                </a>
              </li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-heading font-semibold text-xl mb-4">Contact</h3>
            <ul className="space-y-3">
              <li className="flex items-start">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-3 mt-0.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <a 
                  href={`mailto:${email}`} 
                  className="text-gray-300 hover:text-white transition-colors"
                >
                  {email}
                </a>
              </li>
              <li className="flex items-start">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-3 mt-0.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
                <div className="flex flex-wrap items-center">
                  <span className="text-gray-300">{phoneNumber}</span>
                  {isMobile && (
                    <a 
                      href={`tel:${phoneNumberFormatted}`} 
                      className="ml-2 flex items-center text-maroon hover:text-maroon-light text-sm"
                    >
                      <PhoneCall size={14} className="mr-1" />
                      <span>Call</span>
                    </a>
                  )}
                </div>
              </li>
              <li className="flex items-start">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-3 mt-0.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span className="text-gray-300">Connecticut</span>
              </li>
            </ul>
          </div>
        </div>
        
        <div className="border-t border-gray-700 mt-10 pt-8 flex flex-col md:flex-row justify-between items-center">
          <p className="text-gray-400">© {currentYear} KTJones Management Consulting. All rights reserved.</p>
          <div className="mt-4 md:mt-0">
            <ul className="flex space-x-6">
              <li>
                <span className="text-gray-400 cursor-default text-sm">Privacy Policy</span>
              </li>
              <li>
                <span className="text-gray-400 cursor-default text-sm">Terms of Service</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
